/**
 * Chart builder — turns a user's field/type selections into a Vega-Lite v6 spec.
 *
 * The enterprise chart builder lets the user pick a chart type and map columns to
 * encodings (x / measure+aggregate / series). This module is the pure core: it
 * flattens resolved rows into Vega records and assembles the spec + dark RTL theme.
 * Aggregation is declarative (Vega-Lite `aggregate` in the encoding) so the spec is
 * the single source of truth — no parallel JS aggregator.
 *
 * Pure (no React / no vega) → unit-tested in data-explorer.test.ts.
 */
import { cellText } from './cell-text'
import type { ResolvedColumn, ResolvedRow } from './spec-resolver'

export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'scatter'

/** Vega-Lite aggregate ops we expose (avg is surfaced as 'mean'). */
export type AggregateOp = 'count' | 'sum' | 'mean' | 'median' | 'min' | 'max'

export interface ChartBuilderConfig {
  type: ChartType
  /** column id for the x-axis / category / pie-slice dimension. */
  xColId: string
  /** numeric column id for the measure (omit for `count`; required for scatter). */
  yColId?: string
  /** how to aggregate the measure (ignored for scatter — one mark per row). */
  aggregate: AggregateOp
  /** optional breakdown column id → color/series. */
  seriesColId?: string
  /** optional chart title. */
  title?: string
}

export const DEFAULT_CHART_CONFIG: ChartBuilderConfig = { type: 'bar', xColId: '', aggregate: 'count' }

const PALETTE = ['#274690', '#286F6C', '#D4820A', '#C0392B', '#5B7DB1', '#3FA39E', '#8E7CC3', '#D98C5F']

const THEME = {
  font: 'Vazirmatn, system-ui, sans-serif',
  view: { stroke: 'transparent' },
  axis: { labelColor: '#A8A8A8', titleColor: '#A8A8A8', gridColor: '#3A3A3A', domainColor: '#3A3A3A', labelFont: 'Vazirmatn', titleFont: 'Vazirmatn' },
  legend: { labelColor: '#A8A8A8', titleColor: '#A8A8A8', labelFont: 'Vazirmatn', titleFont: 'Vazirmatn' },
  range: { category: PALETTE },
}

function isNumeric(col: ResolvedColumn | undefined): boolean {
  return !!col && (col.kind === 'calc' || col.fieldType === 'number')
}

/** Which encoding types each chart type supports — drives both UI gating and the spec. */
export function chartTypeCapabilities(type: ChartType) {
  return {
    needsMeasure: type === 'scatter', // scatter needs an explicit numeric Y
    allowsAggregate: type !== 'scatter',
    allowsSeries: type !== 'pie',
    xMustBeNumeric: type === 'scatter',
  }
}

/** Vega-Lite encoding `type` for the x/dimension field. */
function xEncodingType(col: ResolvedColumn | undefined, chartType: ChartType): 'nominal' | 'ordinal' | 'quantitative' {
  if (chartType === 'scatter') return 'quantitative'
  // Dates are stored as zero-padded Jalali display (YYYY/MM/DD…) which sorts
  // chronologically as text — encode as ordinal so axis labels stay Persian.
  if (col?.fieldType === 'date' || col?.fieldType === 'datetime') return 'ordinal'
  if (chartType === 'line' || chartType === 'area') return 'ordinal'
  return 'nominal'
}

/**
 * Flatten resolved rows to Vega records keyed by column id. Measure/scatter-axis
 * columns carry their raw numeric value; everything else carries its display text
 * (so categories/series read in Persian and dates keep Jalali labels).
 */
function toRecords(
  columns: ResolvedColumn[],
  rows: ResolvedRow[],
  numericIds: Set<string>,
): Record<string, unknown>[] {
  const byId = new Map(columns.map((c) => [c.id, c]))
  return rows.map((r) => {
    const rec: Record<string, unknown> = {}
    for (const [id, col] of byId) {
      if (numericIds.has(id)) {
        const v = r.cells[id]
        rec[id] = v === null || v === undefined || v === '' ? null : Number(v)
      } else {
        rec[id] = cellText(col, r)
      }
    }
    return rec
  })
}

export interface BuiltChart {
  spec: Record<string, unknown>
  /** rows that fed the spec (already inline in spec.data; exposed for tests). */
  recordCount: number
}

/**
 * Build a Vega-Lite spec from the config. Returns null when required fields are
 * missing (no x, scatter without a numeric y, non-count without a measure).
 */
export function buildChartSpec(
  config: ChartBuilderConfig,
  columns: ResolvedColumn[],
  rows: ResolvedRow[],
): BuiltChart | null {
  const { type, xColId, yColId, aggregate, seriesColId, title } = config
  const xCol = columns.find((c) => c.id === xColId)
  const yCol = yColId ? columns.find((c) => c.id === yColId) : undefined
  const seriesCol = seriesColId ? columns.find((c) => c.id === seriesColId) : undefined
  const caps = chartTypeCapabilities(type)

  if (!xCol) return null
  if (caps.xMustBeNumeric && !isNumeric(xCol)) return null
  if (caps.needsMeasure && !isNumeric(yCol)) return null
  // non-count aggregation needs a numeric measure column
  if (caps.allowsAggregate && aggregate !== 'count' && !isNumeric(yCol)) return null

  // Columns we must materialize numerically.
  const numericIds = new Set<string>()
  if (caps.xMustBeNumeric) numericIds.add(xCol.id)
  if (yCol && isNumeric(yCol)) numericIds.add(yCol.id)

  const data = toRecords(columns, rows, numericIds)

  // measure encoding (y / theta)
  const measure: Record<string, unknown> =
    type === 'scatter'
      ? { field: yCol!.id, type: 'quantitative', title: yCol!.label }
      : aggregate === 'count'
        ? { aggregate: 'count', type: 'quantitative', title: 'تعداد' }
        : { field: yCol!.id, aggregate, type: 'quantitative', title: `${aggLabel(aggregate)} ${yCol!.label}` }

  const colorEnc = caps.allowsSeries && seriesCol
    ? { color: { field: seriesCol.id, type: 'nominal', title: seriesCol.label } }
    : {}

  const base: Record<string, unknown> = {
    $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
    background: 'transparent',
    width: 'container',
    height: 340,
    autosize: { type: 'fit', contains: 'padding' },
    data: { values: data },
    config: THEME,
    ...(title ? { title } : {}),
  }

  const xType = xEncodingType(xCol, type)
  const xEnc = { field: xCol.id, type: xType, title: xCol.label, ...(xType === 'nominal' && type === 'bar' && !seriesCol ? { sort: '-y' as const } : {}) }

  if (type === 'pie') {
    return {
      spec: {
        ...base,
        mark: { type: 'arc', innerRadius: 60, tooltip: true },
        encoding: {
          theta: measure,
          color: { field: xCol.id, type: 'nominal', title: xCol.label },
        },
      },
      recordCount: data.length,
    }
  }

  const mark =
    type === 'line' ? { type: 'line', point: true, tooltip: true, ...(seriesCol ? {} : { color: PALETTE[0] }) }
    : type === 'area' ? { type: 'area', tooltip: true, opacity: 0.85, ...(seriesCol ? {} : { color: PALETTE[0] }) }
    : type === 'scatter' ? { type: 'point', filled: true, tooltip: true, size: 70, ...(seriesCol ? {} : { color: PALETTE[0] }) }
    : { type: 'bar', tooltip: true, cornerRadiusEnd: 3, ...(seriesCol ? {} : { color: PALETTE[0] }) }

  return {
    spec: {
      ...base,
      mark,
      encoding: { x: xEnc, y: measure, ...colorEnc },
    },
    recordCount: data.length,
  }
}

function aggLabel(op: AggregateOp): string {
  return { count: 'تعداد', sum: 'جمع', mean: 'میانگین', median: 'میانه', min: 'کمینه', max: 'بیشینه' }[op]
}
